import { useEffect, useState } from 'react';
import { ShippingAddressApi, FormattedAddressesResponse } from '@shared/sdk-v1';
import { useAuth } from '../auth/hooks/useAuth';
import { apiConfig } from '../shared/api-config';

interface UseShippingAddressesResult {
  data: FormattedAddressesResponse | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export const useShippingAddresses = (): UseShippingAddressesResult => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [data, setData] = useState<FormattedAddressesResponse | null>(null);
  const { user, courier } = useAuth();

  const fetchAddresses = async () => {
    if (!user?.id || !courier?.id) {
      setError(new Error('User or courier information not available'));
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const api = new ShippingAddressApi(apiConfig());
      const response = await api.shippingAddressGetFormattedAddresses({
        courierId: courier.id.toString(),
        userId: user.id.toString()
      });
      setData(response);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch shipping addresses'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAddresses();
  }, [user?.id, courier?.id]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchAddresses
  };
};
